/**
 * The console's API client.
 *
 * Same-origin by default: admin.routly.app proxies `/api` to the backend, so
 * the session cookie Better Auth sets is first-party and no hostname is baked
 * into the bundle. `VITE_API_URL` overrides it for a developer pointing at a
 * backend somewhere else.
 */
const API_BASE_URL = import.meta.env.VITE_API_URL?.trim() || '/api';

let tokenGetter: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  tokenGetter = getter;
}

/** The validation failures the backend lists under `errors`. */
export interface ApiValidationError {
  field: string;
  message: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** `errorCode` from the body, for a caller that needs the class and not the sentence. */
    public code?: string,
    public errors?: ApiValidationError[],
    /** `traceId`, so a report can name the request the server logged. */
    public traceId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ProblemBody {
  detail?: unknown;
  title?: unknown;
  error?: unknown;
  message?: unknown;
  errorCode?: unknown;
  errors?: unknown;
  traceId?: unknown;
}

/**
 * What the backend said, rather than the status code.
 *
 * Every error the API returns is an RFC 7807 body — `title`, `detail`, `status`,
 * `errorCode`, `traceId` — and this client read `message` and `error`, neither of
 * which the error handler emits. So a 409 that had taken the trouble to say "A
 * vehicle already claims this device" arrived as "Request failed with status
 * 409", and the console's own toasts, which all pass `error.message` through,
 * showed a number for every refusal the product could give a reason for.
 *
 * `detail` before `title`: the title names the class of failure, the detail names
 * this one. `error` stays in the list because two routes answer with a bare
 * `{ error, detail }` of their own, short-circuiting the error handler —
 * `/v1/admin/retention/prune` refusing when pruning is off is one of them.
 */
function serverMessage(body: ProblemBody): string | null {
  for (const candidate of [body.detail, body.title, body.error, body.message]) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate;
  }
  return null;
}

function validationErrors(value: unknown): ApiValidationError[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const parsed = value.flatMap((entry) =>
    entry && typeof entry === 'object' && typeof (entry as ApiValidationError).field === 'string'
      ? [entry as ApiValidationError]
      : []
  );
  return parsed.length > 0 ? parsed : undefined;
}

export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Only when there is something to describe. Fastify refuses a request that
  // declares a JSON body and sends none — "Body cannot be empty when
  // content-type is set to 'application/json'" — so a header set
  // unconditionally turned every bodyless POST, such as syncing a partner's
  // pool or testing its token, into a 400 before it reached the route.
  const headers: Record<string, string> =
    options.body === undefined || options.body === null
      ? {}
      : { 'Content-Type': 'application/json' };

  if (tokenGetter) {
    try {
      const token = await tokenGetter();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // No token: send the request and let the backend answer 401, which the
      // layout already turns into the sign-in screen.
    }
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    // Stated rather than left to the default: a developer pointing
    // `VITE_API_URL` at another origin is cross-origin, where the default is to
    // send no cookie at all, and the session would silently stop travelling.
    credentials: 'include',
    headers: { ...headers, ...options.headers },
  });

  if (!response.ok) {
    const text = await response.text();
    let message = `Request failed with status ${response.status}`;
    let code: string | undefined;
    let errors: ApiValidationError[] | undefined;
    let traceId: string | undefined;
    try {
      const body = JSON.parse(text) as ProblemBody;
      message = serverMessage(body) ?? message;
      if (typeof body.errorCode === 'string') code = body.errorCode;
      if (typeof body.traceId === 'string') traceId = body.traceId;
      errors = validationErrors(body.errors);
    } catch {
      // Not JSON — nginx's own 502 page, or a proxy timeout. The text is still
      // more use than the status on its own.
      if (text.trim()) message = text.trim().slice(0, 300);
    }
    throw new ApiError(response.status, message, code, errors, traceId);
  }

  // 204, and 205/304 for completeness: no body to parse.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
